// Scripted timeline for the /tutorial-demo animated walkthrough.
//
// Every scene is a pure function of (init state + steps up to local time), so the player can
// seek anywhere — a chapter click or a scrub on the progress bar just re-derives the frame.
// Scene order follows the in-app SpotlightTutorial (tutorialSteps.ts), with login in front.
//
// Narration mp3s live in public/media/tutorial/demo and were generated with ElevenLabs
// (eleven_v3, language he), all in ONE voice (iP95p4xoKVk53GoZ742B, stability 0.5,
// similarity 0.75; "צור" sent as "צוֹר"), at most 1.1x sped up. `audioMs` is each clip's measured length; regenerate both
// together if the copy changes.

export type ConnectStage = 'intro' | 'success'
/** The Facebook page shown in the connect browser window. */
export type FbPage = 'login' | 'find' | 'method' | 'code' | 'newpass' | 'checkpoint'
/** What the phone mockup next to the browser is showing. */
export type PhoneState = 'off' | 'sms' | 'approve' | 'approved'

export interface DemoState {
  screen: 'login' | 'app'
  view: 'campaigns' | 'schedule'
  scheduleDone: number
  loginUser: string
  loginPass: string
  loginLoading: boolean

  hasCampaign: boolean
  campaignModal: boolean
  campName: string
  campProfiles: number[]
  campCreating: boolean
  campaignEnabled: boolean
  /** Several campaigns on the page (the "which campaign" scene); activeCamp = selected one. */
  extraCampaigns: boolean
  activeCamp: number

  postModal: boolean
  postName: string
  postContent: string
  postAi: boolean
  postStrength: number
  postMedia: number
  postReply: string
  postSaving: boolean
  hasPost: boolean
  mpEnabled: boolean
  mpFilling: boolean
  mpFilled: boolean

  groupsModal: boolean
  libLoading: boolean
  libOn: boolean
  groupsShown: number
  removedGroup: boolean
  groupCount: number

  connectModal: boolean
  connectStage: ConnectStage
  browserOpen: boolean
  fbPage: FbPage
  fbUser: string
  fbPass: string
  fbBusy: boolean
  fbError: boolean
  fbFind: string
  fbCode: string
  fbNewPass: string
  phone: PhoneState
  connected: boolean

  published: number
  today: number
  supportOpen: boolean
  outro: boolean
}

type TextKey = 'loginUser' | 'loginPass' | 'campName' | 'postName' | 'postContent' | 'postReply' | 'fbUser' | 'fbPass' | 'fbFind' | 'fbCode' | 'fbNewPass'
type NumKey = 'published' | 'today' | 'groupsShown' | 'postMedia' | 'scheduleDone'
export type Target = string | {x: number; y: number}

export type Step =
  | {at: number; set: Partial<DemoState>}
  | {at: number; type: TextKey; text: string; cps: number}
  | {at: number; count: NumKey; from: number; to: number; dur: number}
  | {at: number; move: Target; dur: number}
  | {at: number; click: true}
  | {at: number; scroll: string}
  | {at: number; spot: string | null}

export interface Scene {
  id: string
  title: string
  audio: string
  audioMs: number
  text: string
  init: Partial<DemoState>
  cursorStart?: {x: number; y: number}
  hideCursor?: boolean
  /** Word-level narration timings [word, startMs, endMs] — the reel's captions use them. */
  words?: [string, number, number][]
  /** Multiplies every step's `at` — keeps the actions on the words after a re-voice. */
  stretch?: number
  /** Big on-screen title for the scene (reel only). */
  headline?: string
  steps: Step[]
  durationMs: number
}

export const STAGE_W = 1280
export const STAGE_H = 800

export const BASE_STATE: DemoState = {
  screen: 'app',
  view: 'campaigns',
  scheduleDone: 0,
  loginUser: '',
  loginPass: '',
  loginLoading: false,
  hasCampaign: false,
  campaignModal: false,
  campName: '',
  campProfiles: [0],
  campCreating: false,
  campaignEnabled: false,
  extraCampaigns: false,
  activeCamp: 0,
  postModal: false,
  postName: '',
  postContent: '',
  postAi: false,
  postStrength: 1,
  postMedia: 0,
  postReply: '',
  postSaving: false,
  hasPost: false,
  mpEnabled: false,
  mpFilling: false,
  mpFilled: false,
  groupsModal: false,
  libLoading: true,
  libOn: false,
  groupsShown: 0,
  removedGroup: false,
  groupCount: 0,
  connectModal: false,
  connectStage: 'intro',
  browserOpen: false,
  fbPage: 'login',
  fbUser: '',
  fbPass: '',
  fbBusy: false,
  fbError: false,
  fbFind: '',
  fbCode: '',
  fbNewPass: '',
  phone: 'off',
  connected: false,
  published: 0,
  today: 0,
  supportOpen: false,
  outro: false,
}

// ── Dummy data ────────────────────────────────────────────────────────────────
export const DEMO_PROFILES = [
  {name: 'דוד כהן', avatar: '/media/avatars/300-1.jpg'},
  {name: 'דוד נכסים', avatar: '/media/avatars/300-13.jpg', page: true},
]

export const DEMO_CAMPAIGN_NAME = 'נכסים תל אביב'
export const DEMO_CAMPAIGNS = [DEMO_CAMPAIGN_NAME, 'נכסים רמת גן', 'נכסים חולון']

export const DEMO_POST_TEXT =
  '💫 חדש למכירה בבלעדיות 💫\n⭐️ דירת 4 חדרים משופצת בצפון הישן\n⭐️ מרפסת שמש 12 מ״ר, קומה 3 עם מעלית\n⭐️ 3,950,000 ₪\n📞 לתיאום ביקור בנכס: 050-0000000'

export const DEMO_POST_NAME = 'דירת 4 חדרים - צפון הישן'
// The auto-reply gives the contact details itself (not "sent you a private message").
export const DEMO_POST_REPLY = 'היי! לפרטים ולתיאום ביקור בנכס: 050-0000000 📞'

// What the marketplace AI pulls out of DEMO_POST_TEXT.
export const DEMO_MP = {
  listingType: 'home_sale',
  price: '3,950,000',
  propertyType: 'דירה',
  rooms: '4',
  baths: '2',
  size: '110',
  location: 'תל אביב-יפו',
  description: 'דירת 4 חדרים משופצת בצפון הישן, מרפסת שמש 12 מ״ר, קומה 3 עם מעלית.',
}

export const DEMO_POST_MEDIA = [
  '/media/stock/900x600/27.jpg',
  '/media/stock/900x600/24.jpg',
  '/media/stock/900x600/16.jpg',
]

export const DEMO_GROUPS = [
  {name: 'דירות למכירה בתל אביב', followers: '84K', avatar: '/media/stock/900x600/27.jpg'},
  {name: 'נדל״ן תל אביב והמרכז', followers: '52K', avatar: '/media/stock/900x600/19.jpg'},
  {name: 'דירות בצפון הישן', followers: '18K', avatar: '/media/stock/900x600/16.jpg'},
  {name: 'תיווך ונכסים - גוש דן', followers: '31K', avatar: '/media/stock/900x600/11.jpg'},
  {name: 'דירות יוקרה בתל אביב', followers: '27K', avatar: '/media/stock/600x400/img-29.jpg'},
  {name: 'קונים דירה בתל אביב', followers: '46K', avatar: '/media/stock/900x600/24.jpg'},
]
export const DEMO_LIBRARY_GROUPS = 48

export const DEMO_FB_EMAIL = 'david.cohen@gmail.com'

// Today's slots on the schedule page: which profile posts to which group, and when.
export const DEMO_SCHEDULE = [
  {time: '08:12', profile: 0, group: 0},
  {time: '08:47', profile: 1, group: 1},
  {time: '09:31', profile: 0, group: 3},
  {time: '10:05', profile: 1, group: 5},
  {time: '10:52', profile: 0, group: 4},
  {time: '11:38', profile: 1, group: 2},
  {time: '12:20', profile: 0, group: 1},
]

// ── Step helpers ──────────────────────────────────────────────────────────────
export const set = (at: number, patch: Partial<DemoState>): Step => ({at, set: patch})
export const type = (at: number, key: TextKey, text: string, cps = 14): Step => ({at, type: key, text, cps})
export const count = (at: number, key: NumKey, from: number, to: number, dur: number): Step => ({at, count: key, from, to, dur})
export const move = (at: number, target: Target, dur = 800): Step => ({at, move: target, dur})
export const click = (at: number): Step => ({at, click: true})
export const scroll = (at: number, target: string): Step => ({at, scroll: target})
export const spot = (at: number, target: string | null): Step => ({at, spot: target})
/** Move to `target`, then click on arrival. Returns the steps and the time of the click. */
export const tap = (at: number, target: Target, dur = 800): Step[] => [move(at, target, dur), click(at + dur + 80)]

export const td = (name: string) => `[data-td="${name}"]`

// Campaign ready and the Facebook window already open — where the reset/approve scenes start.
const FB_OPEN: Partial<DemoState> = {
  hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS,
  connectModal: true, connectStage: 'intro', browserOpen: true, fbPage: 'login',
}

// ── Scenes ────────────────────────────────────────────────────────────────────
const rawScenes: Omit<Scene, 'durationMs'>[] = [
  {
    id: 'login',
    title: 'התחברות',
    audio: '/media/tutorial/demo/01-login.mp3?v=3',
    audioMs: 9920,
    stretch: 1.051, // step times were set against the previous, shorter narration
    text: 'ברוכים הבאים ל-FB Zipper! במדריך הקצר הזה נראה איך מתחילים לפרסם. קודם כול מתחברים למערכת: מזינים שם משתמש וסיסמה, ולוחצים על המשך.',
    init: {screen: 'login'},
    cursorStart: {x: 900, y: 620},
    steps: [
      spot(960, td('login-card')),
      spot(3350, null),
      ...tap(3670, td('login-user'), 720),
      type(4540, 'loginUser', 'david@example.com', 16),
      ...tap(5580, td('login-pass'), 480),
      type(6220, 'loginPass', 'demo-pass', 12),
      ...tap(7490, td('login-submit'), 560),
      set(8210, {loginLoading: true}),
      set(9730, {screen: 'app', loginLoading: false}),
    ],
  },
  {
    id: 'campaign',
    title: 'יצירת קמפיין',
    audio: '/media/tutorial/demo/02-campaign.mp3?v=3',
    audioMs: 14280,
    stretch: 1.182, // step times were set against the previous, shorter narration
    text: 'עכשיו ניצור קמפיין. קמפיין הוא תיקייה שמארגנת את הפוסטים לפי עיר או תחום. לוחצים על קמפיין חדש, נותנים לו שם, למשל: נכסים תל אביב. בוחרים את הפרופילים שיפרסמו, ולוחצים על צור קמפיין.',
    init: {},
    cursorStart: {x: 640, y: 420},
    steps: [
      scroll(150, td('campaigns-card')),
      spot(740, td('campaigns-card')),
      spot(3270, td('new-campaign')),
      ...tap(3420, td('new-campaign'), 670),
      set(4240, {campaignModal: true}),
      spot(4240, null),
      ...tap(4910, td('camp-name'), 520),
      type(5580, 'campName', DEMO_CAMPAIGN_NAME, 11),
      spot(7440, td('camp-profiles')),
      ...tap(7660, td('camp-profile-1'), 520),
      set(8260, {campProfiles: [0, 1]}),
      spot(9150, null),
      ...tap(9370, td('camp-submit'), 520),
      set(9970, {campCreating: true}),
      set(10790, {campaignModal: false, campCreating: false, hasCampaign: true}),
      spot(11010, td('campaign-card')),
    ],
  },
  {
    id: 'post',
    title: 'הוספת פוסט',
    audio: '/media/tutorial/demo/03-post.mp3?v=4',
    audioMs: 14120,
    text: 'הקמפיין מוכן, ועכשיו מוסיפים פוסט. נותנים לפוסט שם, כותבים את תוכן המודעה, ואפשר ללחוץ על: שפר עם AI, כדי לשפר את הניסוח. מוסיפים תמונות, וכותבים תגובה אוטומטית, שתישלח לכל מי שמגיב לפוסט.',
    init: {hasCampaign: true},
    cursorStart: {x: 640, y: 300},
    steps: [
      scroll(90, td('posts-section')),
      ...tap(500, td('add-post'), 700),
      set(1350, {postModal: true}),
      // timed to the narration's words (no publishing-frequency step: the field is gone)
      ...tap(2300, td('post-name'), 400),
      type(2900, 'postName', DEMO_POST_NAME, 20),
      ...tap(3800, td('post-content'), 350),
      type(4300, 'postContent', DEMO_POST_TEXT, 90),
      spot(6100, td('post-ai')),
      ...tap(6300, td('post-ai'), 400),
      set(6850, {postAi: true}),
      set(8300, {postAi: false}),
      spot(8400, null),
      scroll(8800, td('post-dropzone')),
      ...tap(9000, td('post-dropzone'), 400),
      count(9550, 'postMedia', 0, 3, 700),
      scroll(10300, td('post-reply')),
      ...tap(10450, td('post-reply'), 400),
      type(11000, 'postReply', DEMO_POST_REPLY, 18),
    ],
  },
  {
    id: 'marketplace',
    title: 'פרסום במרקטפלייס',
    audio: '/media/tutorial/demo/04-marketplace.mp3?v=3',
    audioMs: 12120,
    stretch: 1.01, // step times were set against the previous, shorter narration
    text: 'רוצים לפרסם גם במרקטפלייס? מסמנים את התיבה, והמערכת ממלאת את הפרטים לבד מתוך הפוסט: סוג המודעה, מחיר, חדרים ומיקום. בודקים שהכול נכון, ולוחצים על פרסם.',
    init: {
      hasCampaign: true, postModal: true, postName: DEMO_POST_NAME, postContent: DEMO_POST_TEXT,
      postStrength: 3, postMedia: 3, postReply: DEMO_POST_REPLY,
    },
    cursorStart: {x: 640, y: 560},
    steps: [
      scroll(0, td('posts-section')),
      scroll(50, td('mp-section')),
      spot(300, td('mp-section')),
      ...tap(1600, td('mp-toggle'), 500),
      set(2250, {mpEnabled: true, mpFilling: true}),
      spot(2250, null),
      set(4300, {mpFilling: false, mpFilled: true}),
      scroll(4400, td('mp-type')),
      spot(5800, td('mp-type')),
      spot(6500, td('mp-price')),
      scroll(7100, td('mp-rooms')),
      spot(7300, td('mp-rooms')),
      spot(8000, td('mp-location')),
      spot(8900, null),
      ...tap(9200, td('post-publish'), 700),
      set(10050, {postSaving: true}),
      set(10800, {postModal: false, postSaving: false, hasPost: true}),
      spot(11000, td('post-card')),
    ],
  },
  {
    // Several campaigns: each property goes to its city's campaign — a Tel Aviv flat to
    // "נכסים תל אביב", a Ramat Gan flat: pick "נכסים רמת גן" first, then add the post there.
    // Step times follow the narration's word timings (scribe).
    id: 'campaigns',
    title: 'כמה קמפיינים',
    audio: '/media/tutorial/demo/05b-campaigns.mp3?v=4',
    audioMs: 15960,
    text: 'יש לכם כמה קמפיינים? כל נכס עולה לקמפיין של העיר שלו. יש לכם דירה בתל אביב? מעלים אותה לקמפיין נכסים תל אביב. יש לכם דירה ברמת גן? בוחרים קודם את הקמפיין נכסים רמת גן, ושם לוחצים על הוספת פוסט. ככה כל נכס מתפרסם בקבוצות של העיר שלו.',
    init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS, extraCampaigns: true},
    cursorStart: {x: 640, y: 420},
    steps: [
      scroll(80, td('campaigns-card')),
      spot(500, td('campaigns-card')),
      spot(3700, td('camp-card-0')),
      scroll(5000, td('posts-section')),
      spot(5300, td('add-post-card')),
      scroll(7000, td('campaigns-card')),
      spot(7200, null),
      ...tap(9300, td('camp-card-1'), 700),
      set(10140, {activeCamp: 1}),
      spot(10150, td('camp-card-1')),
      scroll(10950, td('posts-section')),
      spot(11150, td('add-post')),
      ...tap(11300, td('add-post'), 500),
      set(12000, {postModal: true}),
      spot(12050, null),
      spot(13300, td('post-campaign')),
      type(13500, 'postName', 'דירת 3 חדרים - רמת גן', 14),
      spot(15700, null),
    ],
  },
  {
    id: 'groups',
    title: 'בחירת קבוצות',
    audio: '/media/tutorial/demo/05-groups.mp3?v=3',
    audioMs: 11240,
    stretch: 1.011, // step times were set against the previous, shorter narration
    text: 'בלחיצה על קבוצות רואים את הקבוצות של הקמפיין. המערכת כבר בחרה עבורכם אוטומטית ספריית קבוצות שמתאימה לתחום שלכם. אפשר להוסיף או להסיר קבוצות, ובסוף לוחצים על שמור.',
    init: {hasCampaign: true, hasPost: true},
    cursorStart: {x: 700, y: 600},
    steps: [
      scroll(90, td('campaigns-card')),
      spot(520, td('groups-stat')),
      ...tap(600, td('groups-stat'), 780),
      spot(1470, null),
      set(1470, {groupsModal: true, libLoading: true}),
      set(2850, {libLoading: false, libOn: true}),
      spot(3020, td('lib-card')),
      count(3630, 'groupsShown', 0, DEMO_GROUPS.length, 1380),
      spot(5440, null),
      ...tap(5530, td('group-2'), 690),
      set(6290, {removedGroup: true}),
      ...tap(7080, td('group-2'), 350),
      set(7490, {removedGroup: false}),
      ...tap(8460, td('groups-save'), 780),
      set(9320, {groupsModal: false, groupCount: DEMO_LIBRARY_GROUPS}),
      spot(9500, td('groups-stat')),
    ],
  },
  {
    id: 'connect',
    title: 'חיבור פרופיל',
    audio: '/media/tutorial/demo/06-connect.mp3?v=3',
    audioMs: 11240,
    stretch: 1.011, // step times were set against the previous, shorter narration
    text: 'עכשיו מחברים את פרופיל הפייסבוק. לוחצים על התחבר, ונפתח חלון של פייסבוק. מתחברים בדיוק כמו בכל דפדפן: מזינים את המייל או מספר הטלפון, את הסיסמה, ולוחצים על התחברות.',
    init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS},
    cursorStart: {x: 700, y: 500},
    steps: [
      scroll(80, 'top'),
      spot(300, td('profile-card')),
      ...tap(1400, td('connect-btn'), 700),
      set(2250, {connectModal: true, connectStage: 'intro'}),
      spot(2250, null),
      ...tap(2700, td('connect-password'), 500),
      set(3350, {browserOpen: true, fbPage: 'login'}),
      ...tap(4300, td('fb-user'), 500),
      type(4950, 'fbUser', DEMO_FB_EMAIL, 18),
      ...tap(6500, td('fb-pass'), 400),
      type(7050, 'fbPass', 'Dav1d-2024', 14),
      ...tap(8800, td('fb-login'), 500),
      set(9450, {fbBusy: true}),
      set(10500, {fbBusy: false, fbError: true}),
      spot(10600, td('fb-error')),
    ],
  },
  {
    id: 'reset',
    title: 'שכחתי סיסמה',
    audio: '/media/tutorial/demo/07-reset.mp3?v=3',
    audioMs: 12200,
    stretch: 1.017, // step times were set against the previous, shorter narration
    text: 'שכחתם את הסיסמה? לא נורא. לוחצים על: שכחת את הסיסמה, מזינים את המייל או מספר הטלפון, ופייסבוק שולח קוד בהודעת SMS. מזינים את הקוד, בוחרים סיסמה חדשה, וממשיכים.',
    init: {...FB_OPEN, fbUser: DEMO_FB_EMAIL, fbPass: 'Dav1d-2024', fbError: true},
    cursorStart: {x: 640, y: 470},
    steps: [
      spot(200, td('fb-forgot')),
      ...tap(1400, td('fb-forgot'), 600),
      set(2150, {fbPage: 'find'}),
      spot(2150, null),
      ...tap(2500, td('fb-find-input'), 400),
      type(3050, 'fbFind', DEMO_FB_EMAIL, 22),
      ...tap(4200, td('fb-find-search'), 400),
      set(4750, {fbPage: 'method'}),
      ...tap(5300, td('fb-method-continue'), 500),
      set(5950, {fbPage: 'code'}),
      set(6300, {phone: 'sms'}),
      spot(6400, td('phone')),
      spot(7900, null),
      ...tap(7900, td('fb-code-input'), 500),
      type(8550, 'fbCode', '482913', 12),
      ...tap(9150, td('fb-code-continue'), 300),
      set(9600, {fbPage: 'newpass', phone: 'off'}),
      ...tap(9900, td('fb-newpass-input'), 300),
      type(10350, 'fbNewPass', 'NewPass-2025', 16),
      ...tap(11200, td('fb-newpass-continue'), 400),
      set(11750, {fbPage: 'login', fbPass: '', fbError: false}),
    ],
  },
  {
    id: 'approve',
    title: 'אישור בטלפון',
    audio: '/media/tutorial/demo/08-approve.mp3?v=3',
    audioMs: 10240,
    stretch: 1.123, // step times were set against the previous, shorter narration
    text: 'עכשיו מתחברים שוב, רגיל, עם הסיסמה החדשה. אם פייסבוק מבקש לאשר שזה אתם, מגיעה התראה לטלפון. לוחצים על: כן, זה אני, והפרופיל מחובר!',
    init: {...FB_OPEN, fbUser: DEMO_FB_EMAIL},
    cursorStart: {x: 640, y: 470},
    steps: [
      ...tap(300, td('fb-pass'), 500),
      type(950, 'fbPass', 'NewPass-2025', 18),
      ...tap(1900, td('fb-login'), 500),
      set(2550, {fbBusy: true}),
      set(3300, {fbBusy: false, fbPage: 'checkpoint'}),
      spot(3400, td('fb-checkpoint')),
      set(4700, {phone: 'approve'}),
      spot(4800, td('phone')),
      ...tap(5600, td('phone-yes'), 700),
      set(6450, {phone: 'approved'}),
      spot(6450, null),
      set(7300, {browserOpen: false, phone: 'off', connectStage: 'success', connected: true}),
      ...tap(8000, td('connect-done'), 500),
      set(8650, {connectModal: false, campaignEnabled: true}),
      spot(8700, td('profile-card')),
    ],
  },
  {
    id: 'tour',
    title: 'ניהול הקמפיין',
    audio: '/media/tutorial/demo/09-tour.mp3?v=3',
    audioMs: 12160,
    stretch: 1.126, // step times were set against the previous, shorter narration
    text: 'זהו, הכול מוכן! בכרטיס הקמפיין רואים כמה פוסטים וקבוצות יש בו, כמה פרסומים בוצעו מאז ההתחלה, וכמה היום. והמתג הזה מפעיל או משהה את הפרסום בלחיצה אחת.',
    init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS, connected: true, campaignEnabled: true},
    cursorStart: {x: 640, y: 500},
    steps: [
      scroll(80, td('campaigns-card')),
      spot(750, td('campaign-card')),
      spot(1990, td('posts-stat')),
      spot(2730, td('groups-stat')),
      spot(3810, td('published-stat')),
      count(3890, 'published', 0, 1284, 1330),
      spot(5550, td('today-stat')),
      count(5630, 'today', 0, 36, 830),
      move(6790, td('campaign-toggle'), 750),
      spot(7450, td('campaign-toggle')),
      click(8450),
      set(8510, {campaignEnabled: false}),
      click(9520),
      set(9590, {campaignEnabled: true}),
      spot(10930, null),
    ],
  },
  {
    id: 'automation',
    title: 'איך זה עובד',
    audio: '/media/tutorial/demo/10-automation.mp3?v=3',
    audioMs: 13520,
    stretch: 1.03, // step times were set against the previous, shorter narration
    text: 'ומה קורה מאחורי הקלעים? המערכת מתזמנת את הפוסטים שלכם לאורך היום, ומפרסמת אותם אוטומטית מהפרופילים שלכם, קבוצה אחרי קבוצה, בקצב בטוח. אתם רק מוסיפים פוסטים, והמערכת עושה את כל השאר.',
    init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS, connected: true, campaignEnabled: true, published: 1284, today: 36},
    cursorStart: {x: 640, y: 500},
    steps: [
      spot(250, td('menu-schedule')),
      ...tap(410, td('menu-schedule'), 740),
      set(1210, {view: 'schedule'}),
      spot(1230, null),
      scroll(1230, 'top'),
      count(2130, 'scheduleDone', 0, 6, 10250),
      spot(2460, td('sched-0')),
      spot(5740, td('sched-2')),
      spot(9020, td('sched-4')),
      spot(12460, null),
    ],
  },
  {
    id: 'support',
    title: 'תמיכה',
    audio: '/media/tutorial/demo/11-support.mp3?v=3',
    audioMs: 6200,
    stretch: 1.02, // step times were set against the previous, shorter narration
    text: 'ואם יש שאלה, לוחצים על כפתור התמיכה, וצוות FB Zipper יחזור אליכם בהקדם. בהצלחה!',
    init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS, connected: true, campaignEnabled: true, published: 1284, today: 36},
    cursorStart: {x: 640, y: 500},
    steps: [
      spot(350, td('support-btn')),
      ...tap(700, td('support-btn'), 870),
      set(1660, {supportOpen: true}),
      spot(1660, null),
      set(6460, {outro: true}),
    ],
  },
]

/** A scene lasts until its narration and its last step are both done, plus `tailMs`. */
export const finishScenes = (raw: Omit<Scene, 'durationMs'>[], tailMs = 900): Scene[] =>
  raw.map((r) => {
    const s = r.stretch ? {...r, steps: r.steps.map((st) => ({...st, at: Math.round(st.at * r.stretch!)}))} : r
    const lastStep = Math.max(...s.steps.map((st) => st.at + ('dur' in st ? st.dur : 0)))
    return {...s, durationMs: Math.max(s.audioMs + tailMs, lastStep + tailMs)}
  })

export const makeTimeline = (scenes: Scene[]) => {
  const total = scenes.reduce((sum, s) => sum + s.durationMs, 0)
  const sceneAt = (t: number): {index: number; local: number} => {
    let acc = 0
    for (let i = 0; i < scenes.length; i++) {
      if (t < acc + scenes[i].durationMs || i === scenes.length - 1) {
        return {index: i, local: Math.min(t - acc, scenes[i].durationMs)}
      }
      acc += scenes[i].durationMs
    }
    return {index: 0, local: 0}
  }
  const sceneStart = (index: number) => scenes.slice(0, index).reduce((sum, s) => sum + s.durationMs, 0)
  return {total, sceneAt, sceneStart}
}

export const SCENES: Scene[] = finishScenes(rawScenes)

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))
export const easeInOut = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2)

/** The UI state of `scene` at `local` ms — init, then every step that has started. */
export const deriveState = (scene: Scene, local: number): DemoState => {
  const s: DemoState = {...BASE_STATE, ...scene.init}
  for (const step of scene.steps) {
    if (step.at > local) continue
    if ('set' in step) Object.assign(s, step.set)
    else if ('type' in step) {
      const chars = Array.from(step.text)
      s[step.type] = chars.slice(0, Math.floor(((local - step.at) * step.cps) / 1000)).join('')
    } else if ('count' in step) {
      const p = easeInOut(clamp01((local - step.at) / step.dur))
      s[step.count] = Math.round(step.from + (step.to - step.from) * p)
    }
  }
  return s
}
